
import React, { useEffect, useRef } from "react";

const DCODE_PRIMARY = "#75c4c0";
const DCODE_DANGER = "#f88381";

const Step3_Configure = ({ thresholds, setThresholds, selectedMetric }) => {
    if (!selectedMetric) return null;

    const isFitness = selectedMetric === "fitness";
    const metricName = isFitness ? "Fitness Score (F)" : "Code Smell Density (S)";
    const absKey = isFitness ? "fitness_absolute" : "smell_absolute";
    const relKey = isFitness ? "fitness_relative" : "smell_relative";

    // Helper to update absolute threshold value
    const handleAbsValueChange = (e) => {
      setThresholds(prev => ({
        ...prev,
        [absKey]: {
          ...prev[absKey],
          value: parseFloat(e.target.value) || 0
        }
      }));
    };

    const handleAbsSwitchChange = (e) => {
      const isChecked = e.target.checked;
      setThresholds(prev => ({
        ...prev,
        // Set the absolute threshold state
        [absKey]: {
          ...prev[absKey],
          enabled: isChecked
        },
        // If absolute is enabled, disable relative
        [relKey]: {
          ...prev[relKey],
          enabled: isChecked ? false : prev[relKey].enabled
        }
      }));
    };

    const handleRelSwitchChange = (e) => {
      const isChecked = e.target.checked;
      setThresholds(prev => ({
        ...prev,
        // Set the relative threshold state
        [relKey]: {
          ...prev[relKey],
          enabled: isChecked
        },
        // IMPORTANT: If relative is enabled, disable absolute
        [absKey]: {
          ...prev[absKey],
          enabled: isChecked ? false : prev[absKey].enabled
        }
      }));
    };
    // END OF MUTUALLY EXCLUSIVE HANDLERS

    return (
      <div className="p-4">
        {/* <h3 className="text-dark fw-bold mb-5">3. Configure Thresholds for <span className="text-primary">{metricName}</span></h3> */}
        <p className="text-muted mb-4">Define the condition and corresponding threshold(s) that a commit must satisfy to pass the quality gate.</p>

        {/* Absolute Threshold */}
        <div
          className={`card mb-4 shadow-sm ${thresholds[absKey].enabled ? 'border-3' : 'border-light'}`}
          style={{ borderColor: thresholds[absKey].enabled ? DCODE_PRIMARY : '' }} // Use DCODE_PRIMARY for enabled border
        >
          <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
            <h5 className="mb-0 fw-bold">Absolute threshold</h5>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="absolute-enable"
                checked={thresholds[absKey].enabled}
                onChange={handleAbsSwitchChange} // UPDATED HANDLER
              />
              <label className="form-check-label fw-bold text-muted" htmlFor="absolute-enable">
                {thresholds[absKey].enabled ? <span style={{ color: DCODE_PRIMARY }}>ENABLED</span> : "DISABLED"}
              </label>
            </div>
          </div>
          <div className="card-body">
            <p className="mb-3">
              <strong className="text-dark">{isFitness ? "Current fitness score ≥ threshold" : "Current smell density ≤ threshold"}</strong><br />
              <small className="text-muted">
                The current value of the selected metric must meet or beat a fixed threshold.
              </small>
            </p>
            {thresholds[absKey].enabled && (
              <div style={{ maxWidth: '400px' }}>
                <label className="form-label fw-bold d-flex justify-content-between">
                  <span>Threshold value</span>
                  {/* Display the selected value prominently */}
                  <span className="badge text-white" style={{ backgroundColor: DCODE_PRIMARY, fontSize: '1rem' }}>
                    {thresholds[absKey].value.toFixed(2)}
                  </span>
                </label>

                {/* Conditional Rendering for Input Type */}
                {isFitness ? (
                  // **Range Input for Fitness Score (0 to 1)**
                  <>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01" // Allows for precise selection
                      className="form-range" // Bootstrap range input style
                      value={thresholds[absKey].value}
                      onChange={handleAbsValueChange}
                    />
                    <div className="d-flex justify-content-between small text-muted mt-1">
                      <span>0.00 (Poor)</span>
                      <span>1.00 (Perfect)</span>
                    </div>
                  </>
                ) : (
                  // **Number Input for Code Smell Density**
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    value={thresholds[absKey].value}
                    onChange={handleAbsValueChange}
                    placeholder="5"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Relative Threshold */}
        <div
          className={`card shadow-sm ${thresholds[relKey].enabled ? 'border-3' : 'border-light'}`}
          style={{ borderColor: thresholds[relKey].enabled ? DCODE_PRIMARY : '' }}
        >
          <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
            <h5 className="mb-0 fw-bold">Relative threshold (Prevent regression)</h5>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="relative-enable"
                checked={thresholds[relKey].enabled}
                onChange={handleRelSwitchChange}
              />
              <label className="form-check-label fw-bold text-muted" htmlFor="relative-enable">
                {thresholds[relKey].enabled ? <span style={{ color: DCODE_PRIMARY }}>ENABLED</span> : "DISABLED"}
              </label>
            </div>
          </div>
          <div className="card-body">
            <p className="mb-3">
              <strong className="text-dark">{isFitness ? "Current fitness score ≥ maximum fitness score observed over the specified number of previous commits - Leniency offset" : "Current smell density ≤ minimum code smell density observed over the specified number of previous commits + Leniency offset"}</strong><br />
              <small className="text-muted">
                The current value of the selected metric must be at least as good as the worst value observed over the selected number of previous commits, considering a leniency offset.
              </small>
            </p>
            {thresholds[relKey].enabled && (
              <div className="row g-3" style={{ maxWidth: '600px' }}>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Commits</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="form-control"
                    value={thresholds[relKey].Y}
                    onChange={(e) =>
                      setThresholds(prev => ({
                        ...prev,
                        [relKey]: {
                          ...prev[relKey],
                          Y: Math.min(10, Math.max(1, Number(e.target.value) || 1))
                        }
                      }))
                    }
                    placeholder="1"
                  />
                  <small className="text-muted">Number of previous commits to compare against.</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Leniency offset</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max={isFitness ? 0.25 : undefined}
                    className="form-control"
                    value={thresholds[relKey].L}
                    onChange={(e) => {
                      const val = e.target.value;

                      // Allow empty input while typing
                      if (val === "") {
                        setThresholds(prev => ({
                          ...prev,
                          [relKey]: {
                            ...prev[relKey],
                            L: ""
                          }
                        }));
                        return;
                      }

                      const num = Number(val);

                      // Block non-numeric input
                      if (Number.isNaN(num)) return;

                      // Enforce max constraint manually
                      if (isFitness && num > 0.25) return;

                      setThresholds(prev => ({
                        ...prev,
                        [relKey]: {
                          ...prev[relKey],
                          L: val
                        }
                      }));
                    }}
                    onBlur={() => {
                      // Optional: normalize value on blur
                      setThresholds(prev => {
                        const val = prev[relKey].L;
                        if (val === "") return prev;

                        return {
                          ...prev,
                          [relKey]: {
                            ...prev[relKey],
                            L: Math.min(Number(val), isFitness ? 0.25 : Number(val))
                          }
                        };
                      });
                    }}
                    placeholder={isFitness ? "0.2" : "1.0"}
                  />
                  <small className="text-muted">Allowed deviation margin.</small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

export default Step3_Configure;